const ROLE_ALIASES = Object.freeze({ worker: 'clerk', accounting: 'finance' });

const FIELD_DEFINITIONS = Object.freeze({
  eta_at: { label: 'ETA', type: 'datetime-local' },
  ocean_status: {
    label: 'Ocean Status', type: 'select', options: [
      ['not_arrived', 'Not Arrived'], ['arrived', 'Arrived'], ['available', 'Available'],
      ['pending', 'Pending'], ['delivered_to_warehouse', 'Delivered to Warehouse'],
      ['empty_returned', 'Empty Returned'], ['cancelled', 'Cancelled'],
    ],
  },
  lfd_at: { label: 'LFD', type: 'datetime-local' },
  ctf_status: { label: 'CTF Status', type: 'select', options: [['paid', 'Paid'], ['unpaid', 'Unpaid'], ['not_applicable', 'Not Applicable']] },
  tmf_status: { label: 'TMF Status', type: 'select', options: [['paid', 'Paid'], ['unpaid', 'Unpaid'], ['not_applicable', 'Not Applicable']] },
  customer_name: { label: 'Customer', type: 'text' },
  container_no: { label: 'Container #', type: 'text' },
  size_type: { label: 'Container Size', type: 'select', options: [['20', '20'], ['40HQ', '40HQ'], ['45', '45'], ['Custom', 'Custom']] },
  weight_lbs: { label: 'Weight (lbs)', type: 'number', min: '0.01', step: '0.01' },
  pre_alert_status: { label: 'Pre-Alert Status', type: 'select', options: [['Pre-Alert', 'Pre-Alert'], ['Pre-Alert + DO', 'Pre-Alert + DO']] },
  master_bl_no: { label: 'MBL #', type: 'text' },
  do_ref: { label: 'Delivery Order', type: 'text' },
  delivery_warehouse_address: { label: 'Delivery Warehouse Address', type: 'textarea' },
});

const PREFILL_FIELDS = Object.freeze({
  clerk: Object.freeze(['eta_at', 'ocean_status', 'lfd_at']),
  finance: Object.freeze(['ctf_status', 'tmf_status']),
});

const CUSTOMER_CHANGE_FIELDS = Object.freeze([
  'customer_name', 'container_no', 'size_type', 'weight_lbs', 'pre_alert_status',
  'master_bl_no', 'do_ref', 'delivery_warehouse_address',
]);

export class ContainerTaskDetailUiError extends Error {
  constructor(code, field = null) {
    super(code);
    this.name = 'ContainerTaskDetailUiError';
    this.code = code;
    this.field = field;
  }
}

function fail(code, field = null) {
  throw new ContainerTaskDetailUiError(code, field);
}

function record(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function canonicalRole(value) {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return ROLE_ALIASES[normalized] || normalized;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

function isPresent(value) {
  return value !== null && value !== undefined && value !== '';
}

function localDateTimeValue(value) {
  if (!Number.isSafeInteger(value) || value < 0) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function displayValue(field, value) {
  if (!isPresent(value)) return 'Not provided';
  if (field === 'eta_at' || field === 'lfd_at') {
    const date = new Date(value);
    // A bare toLocaleString() takes the machine's default hour cycle, which on
    // en-US is 12-hour - the last place in the product still printing "11:45 PM"
    // (Alan, 2026-08-11). Stated, so it does not depend on whose laptop it is.
    return Number.isNaN(date.getTime()) ? 'Not provided' : date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function fieldControl(field, value, { disabled = false, required = false } = {}) {
  const definition = FIELD_DEFINITIONS[field];
  if (!definition) return '';
  const attributes = `${disabled ? ' disabled' : ''}${required ? ' required aria-required="true"' : ''}`;
  const safeField = escapeHtml(field);
  const safeLabel = escapeHtml(definition.label);
  if (definition.type === 'select') {
    const legacyOptions = field === 'size_type' && String(value ?? '') === '40'
      ? [['40', '40 (legacy)']] : [];
    const options = [['', 'Select'], ...legacyOptions, ...(definition.options || [])]
      .map(([optionValue, label]) => `<option value="${escapeHtml(optionValue)}" ${String(value ?? '') === optionValue ? 'selected' : ''}>${escapeHtml(label)}</option>`)
      .join('');
    return `<label class="task-detail-field"><span>${safeLabel}</span><select name="${safeField}" data-task-field="${safeField}"${attributes}>${options}</select></label>`;
  }
  if (definition.type === 'textarea') {
    return `<label class="task-detail-field task-detail-field--wide"><span>${safeLabel}</span><textarea name="${safeField}" data-task-field="${safeField}"${attributes}>${escapeHtml(value ?? '')}</textarea></label>`;
  }
  const inputValue = definition.type === 'datetime-local' ? localDateTimeValue(value) : String(value ?? '');
  return `<label class="task-detail-field"><span>${safeLabel}</span><input type="${escapeHtml(definition.type)}" name="${safeField}" data-task-field="${safeField}" value="${escapeHtml(inputValue)}"${definition.min ? ` min="${definition.min}"` : ''}${definition.step ? ` step="${definition.step}"` : ''}${attributes}></label>`;
}

function summaryMarkup(detail) {
  const summary = record(detail.summary);
  const items = [
    ['Customer', summary.customer_name],
    ['Status', summary.status],
    ['Current Stage', summary.current_stage],
    ['Responsible Role', detail.current_action?.responsible_role || 'Waiting'],
    ['Next Action', detail.current_action?.action_type || 'No action required'],
    ['Assigned Driver', summary.assigned_driver_name],
  ];
  return `<dl class="task-detail-summary">${items.map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value || 'Not provided')}</dd></div>`).join('')}</dl>`;
}

function visibleInformationMarkup(detail, ownedFields) {
  const visible = record(detail.visible_fields);
  const rows = Object.entries(visible)
    .filter(([field]) => FIELD_DEFINITIONS[field] && !ownedFields.has(field))
    .map(([field, value]) => `<div class="task-detail-readonly"><span>${escapeHtml(FIELD_DEFINITIONS[field].label)}</span><strong>${escapeHtml(displayValue(field, value))}</strong></div>`);
  return rows.length ? rows.join('') : '<p class="muted">No additional information is available for your role.</p>';
}

function roleInformationMarkup(detail, role) {
  const information = Array.isArray(detail.role_information) ? detail.role_information : [];
  const ownedGroups = role === 'admin'
    ? information.filter((item) => item && Array.isArray(item.fields))
    : information.filter((item) => item?.role === role && Array.isArray(item.fields));
  if (!ownedGroups.length) {
    return { ownedFields: new Set(), markup: '<p class="muted">This role has no editable information in this workflow stage.</p>' };
  }
  const ownedFields = new Set(ownedGroups.flatMap((owned) => owned.fields.map((item) => item.field)));
  const markup = ownedGroups.map((owned) => {
    const current = owned.fields.filter((item) => item.currently_required === true);
    const completed = owned.fields.filter((item) => item.currently_required !== true && isPresent(item.value));
    const future = owned.fields.filter((item) => item.currently_required !== true && !isPresent(item.value));
    const currentMarkup = current.length
      ? `<div class="task-detail-field-grid">${current.map((item) => fieldControl(item.field, item.value, { disabled: true })).join('')}</div>${detail.current_action?.action_id ? `<a class="button" href="#/action/${encodeURIComponent(detail.current_action.action_id)}">Open Required Action</a>` : ''}`
      : '<p class="muted">No information is required from this role right now.</p>';
    const editable = [...completed, ...future];
    const prefillMarkup = editable.length ? `
      <form class="task-detail-prefill-form" data-task-prefill-form data-target-role="${escapeHtml(owned.role)}" data-revision="${Number(detail.information_revision || 0)}">
        ${completed.length ? `<h3>Completed ${escapeHtml(owned.role)} Information</h3><p class="muted">Existing values are editable. Every update requires a note and is audited.</p><div class="task-detail-field-grid">${completed.map((item) => fieldControl(item.field, item.value)).join('')}</div>` : ''}
        ${future.length ? `<h3>Future ${escapeHtml(owned.role)} Information</h3><p class="muted">Unlock only to prefill this role's future information. Workflow order will not change.</p><div class="task-detail-field-grid">${future.map((item) => fieldControl(item.field, item.value, { disabled: true })).join('')}</div><button type="button" class="secondary" data-unlock-future>Edit / Prefill Future Information</button>` : ''}
        <label class="task-detail-field task-detail-field--wide"><span>Update note</span><textarea name="update_note" minlength="3" maxlength="500" required placeholder="Describe why you are updating or prefilling this information."></textarea></label>
        <p class="form-error" data-prefill-error role="alert"></p>
        <button type="submit">Save ${escapeHtml(owned.role)} Information</button>
      </form>` : '<p class="muted">No completed or future fields are available for this role.</p>';
    return `<section class="task-detail-subsection" data-role-information="${escapeHtml(owned.role)}"><h3>${escapeHtml(owned.role)} Information</h3><h4>Current Required Information</h4>${currentMarkup}${prefillMarkup}</section>`;
  }).join('');
  return {
    ownedFields,
    markup,
  };
}

function pendingChangeMarkup(detail, role) {
  const requests = Array.isArray(detail.pending_change_requests) ? detail.pending_change_requests : [];
  if (!requests.length) return '<p class="muted">No pending Customer change requests.</p>';
  return `<div class="task-change-list">${requests.map((item) => `
    <article class="task-change-card" data-change-request-id="${escapeHtml(item.change_request_id)}" data-request-revision="${Number(item.revision || 0)}">
      <h3>${escapeHtml(FIELD_DEFINITIONS[item.field]?.label || item.field)}</h3>
      <p><b>Current:</b> ${escapeHtml(displayValue(item.field, item.current_value))}</p>
      <p><b>Requested:</b> ${escapeHtml(displayValue(item.field, item.proposed_value))}</p>
      <p><b>Customer reason:</b> ${escapeHtml(item.reason)}</p>
      ${(role === 'dispatcher' || role === 'admin') ? `<label class="task-detail-field"><span>Decision note (required for approval)</span><textarea data-decision-note maxlength="500"></textarea></label><div class="actions"><button type="button" data-change-decision="approve">Approve</button><button type="button" class="danger" data-change-decision="reject">Reject</button></div><p class="form-error" data-decision-error role="alert"></p>` : '<span class="status">Pending review</span>'}
    </article>`).join('')}</div>`;
}

function requestChangeMarkup(detail, role) {
  if (role !== 'customer' || detail.can_request_change !== true) return '';
  const visible = record(detail.visible_fields);
  const options = CUSTOMER_CHANGE_FIELDS.filter((field) => Object.prototype.hasOwnProperty.call(visible, field))
    .map((field) => `<option value="${field}">${escapeHtml(FIELD_DEFINITIONS[field].label)}</option>`).join('');
  if (!options) return '';
  return `<section class="panel task-detail-section"><h2>Request Change</h2><p>Because another department has started work, workflow-impacting changes require Dispatcher or Admin approval.</p>
    <form data-customer-change-form data-revision="${Number(detail.information_revision || 0)}">
      <label class="task-detail-field"><span>Field</span><select name="field" required><option value="">Select</option>${options}</select></label>
      <div data-change-value-host></div>
      <label class="task-detail-field task-detail-field--wide"><span>Reason</span><textarea name="reason" minlength="3" maxlength="500" required></textarea></label>
      <p class="form-error" data-change-error role="alert"></p><button type="submit">Submit Edit Request</button>
    </form></section>`;
}

export function renderContainerTaskDetailMarkup(rawDetail, options = {}) {
  const detail = record(rawDetail);
  const role = canonicalRole(options.role);
  const summary = record(detail.summary);
  const containerNo = summary.container_no || detail.container_id || 'Container';
  const roleInformation = roleInformationMarkup(detail, role);
  return `<main class="container-task-detail" data-container-task-detail data-container-id="${escapeHtml(detail.container_id)}" data-information-revision="${Number(detail.information_revision || 0)}">
    <header class="task-detail-header"><div><p class="eyebrow">Container Task</p><h1>${escapeHtml(containerNo)}</h1><p>Role-safe operational detail for ${escapeHtml(role || 'user')}.</p></div><span class="status">${escapeHtml(summary.status || 'pending')}</span></header>
    <section class="panel task-detail-section"><h2>Task Summary</h2>${summaryMarkup(detail)}</section>
    <section class="panel task-detail-section"><h2>My Information</h2>${roleInformation.markup}</section>
    <section class="panel task-detail-section"><h2>Other Authorized Information</h2><div class="task-detail-readonly-grid">${visibleInformationMarkup(detail, roleInformation.ownedFields)}</div></section>
    ${(role === 'dispatcher' || role === 'admin' || role === 'customer') ? `<section class="panel task-detail-section"><h2>Customer Change Requests</h2>${pendingChangeMarkup(detail, role)}</section>` : ''}
    ${requestChangeMarkup(detail, role)}
  </main>`;
}

function normalizedValue(field, value) {
  if (field === 'eta_at' || field === 'lfd_at') {
    const millis = Date.parse(String(value || ''));
    if (!Number.isSafeInteger(millis) || millis < 0) return fail('FIELD_INVALID', field);
    return millis;
  }
  if (field === 'weight_lbs') {
    const number = Number(value);
    if (!Number.isFinite(number) || number <= 0) return fail('FIELD_INVALID', field);
    return number;
  }
  const text = String(value ?? '').trim();
  if (!text && field !== 'master_bl_no' && field !== 'do_ref') return fail('FIELD_REQUIRED', field);
  return text || null;
}

function note(value, field, optional = false) {
  const normalized = String(value ?? '').trim().replace(/\s+/gu, ' ');
  if (!normalized && optional) return null;
  if (normalized.length < 3 || normalized.length > 500) return fail('NOTE_REQUIRED', field);
  return normalized;
}

export function buildContainerPrefillRequest(input, context) {
  const role = canonicalRole(input?.target_role);
  const allowed = new Set(PREFILL_FIELDS[role] || []);
  const baseline = record(context?.baseline);
  const rawValues = record(input?.values);
  const values = {};
  for (const [field, rawValue] of Object.entries(rawValues)) {
    if (!allowed.has(field)) return fail('FIELD_FORBIDDEN', field);
    const value = normalizedValue(field, rawValue);
    if (String(value ?? '') !== String(baseline[field] ?? '')) values[field] = value;
  }
  if (!Object.keys(values).length) return fail('NO_CHANGES');
  return {
    container_id: String(context?.containerId || ''),
    target_role: role,
    expected_revision: Number(context?.revision),
    values,
    update_note: note(input?.update_note, 'update_note'),
    request_id: String(context?.requestId || ''),
  };
}

export function buildCustomerChangeRequest(input, context) {
  const field = String(input?.field || '');
  if (!CUSTOMER_CHANGE_FIELDS.includes(field)) return fail('FIELD_FORBIDDEN', 'field');
  return {
    container_id: String(context?.containerId || ''),
    expected_container_revision: Number(context?.revision),
    field,
    proposed_value: normalizedValue(field, input?.proposed_value),
    reason: note(input?.reason, 'reason'),
    request_id: String(context?.requestId || ''),
  };
}

export function buildChangeDecisionRequest(input, context) {
  const decision = input?.decision;
  if (decision !== 'approve' && decision !== 'reject') return fail('DECISION_INVALID', 'decision');
  return {
    change_request_id: String(context?.changeRequestId || ''),
    expected_request_revision: Number(context?.requestRevision),
    expected_container_revision: Number(context?.containerRevision),
    decision,
    decision_note: note(input?.decision_note, 'decision_note', decision === 'reject'),
    request_id: String(context?.requestId || ''),
  };
}

export function customerChangeValueControl(field, currentValue = null) {
  return CUSTOMER_CHANGE_FIELDS.includes(field)
    ? fieldControl(field, currentValue, { required: !['master_bl_no', 'do_ref'].includes(field) })
      .replace(`name="${field}"`, 'name="proposed_value"')
    : '';
}

export function taskDetailFieldLabel(field) {
  return FIELD_DEFINITIONS[field]?.label || field;
}
